/**
 * The four charts of the project performance section.
 *
 * Each one answers a single management question - are we on budget, which phase is consuming
 * capital, which categories drift, and when is money being spent. Budget and Actual are data
 * series and are coloured as such: neutral for the plan, primary for reality. Red and green are
 * reserved for variance, where they mean something.
 */
import React, { useState } from 'react';
import {
  BarChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import { money, compactMoney, percent, usedPercent, varianceOf, monthLabel } from './financeFormat';

const COLORS = {
  budget: 'rgb(209,213,219)',
  budgetHover: 'rgb(186,191,199)',
  actual: 'rgb(215,57,134)',
  actualSoft: 'rgba(215,57,134,0.5)',
  axis: 'rgba(26,26,26,0.35)',
  grid: 'rgba(26,26,26,0.07)',
};

const AXIS = { fontSize: 11, fill: 'rgba(26,26,26,0.55)' };

// One plot height for every chart, so the pairs line up side by side
const ROW_HEIGHT = 320;

const SeriesKey = () => (
  <div className="flex items-center gap-4 text-xs text-foreground-muted">
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS.budget }} />
      Budget
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS.actual }} />
      Actual
    </span>
  </div>
);

export const ChartFrame = ({ title, subtitle, context, children, height, footer, testId, showKey = true }) => (
  <section
    className="bg-white rounded-xl border border-black/[0.06] p-5 lg:p-6 flex flex-col min-w-0 h-full"
    data-testid={testId}
  >
    {/* Half-width cards leave no room for a right-aligned key, so every chart states its series
        in the same place: directly above the plot. */}
    <header>
      <h3 className="text-base font-semibold leading-snug">{title}</h3>
      {subtitle && <p className="text-[13px] text-foreground-muted mt-1">{subtitle}</p>}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2.5">
        {showKey && <SeriesKey />}
        {context && <p className="text-[13px] tabular-nums text-foreground-muted">{context}</p>}
      </div>
    </header>

    {/* flex-1 would win over the fixed height in a column flex parent and collapse the chart */}
    <div className="mt-4" style={{ height }}>
      {children}
    </div>

    {footer && <div className="mt-4 text-xs text-foreground-muted">{footer}</div>}
  </section>
);

const EmptyChart = ({ headline, body, action }) => (
  <div className="h-full flex flex-col items-center justify-center text-center gap-2 px-6">
    <p className="text-sm font-medium">{headline}</p>
    <p className="text-[13px] text-foreground-muted max-w-sm">{body}</p>
    {action}
  </div>
);

/** Budget, actual, variance and budget used - so nobody has to do the arithmetic. */
const VarianceTooltip = ({ active, payload, label, currency, income = false }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const hasActual = row.actual !== null && row.actual !== undefined;
  const variance = varianceOf(row.budget, row.actual, income);
  const used = usedPercent(row.actual, row.budget);

  return (
    <div className="rounded-xl border border-black/[0.08] bg-white px-3.5 py-2.5 shadow-lg min-w-[180px]">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">
        {row.label || label}
      </p>
      <dl className="mt-2 space-y-1 text-[13px]">
        <div className="flex items-center justify-between gap-6">
          <dt className="text-foreground-muted">Budget</dt>
          <dd className="tabular-nums">{money(row.budget, { currency })}</dd>
        </div>
        <div className="flex items-center justify-between gap-6">
          <dt className="text-foreground-muted">Actual</dt>
          <dd className="tabular-nums font-medium">
            {hasActual ? money(row.actual, { currency }) : 'Not posted yet'}
          </dd>
        </div>
        {hasActual && !!row.budget && (
          <>
            <div className="flex items-center justify-between gap-6">
              <dt className="text-foreground-muted">Variance</dt>
              <dd className={cn('tabular-nums', variance.favourable ? 'text-emerald-600' : 'text-red-600')}>
                {money(Math.abs(variance.diff), { currency })} {variance.favourable ? 'favourable' : 'unfavourable'}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-6">
              <dt className="text-foreground-muted">Budget used</dt>
              <dd className="tabular-nums">{percent(used)}</dd>
            </div>
          </>
        )}
      </dl>
    </div>
  );
};

const barsOf = (rows) => rows.some((r) => r.budget || r.actual);

/** Phase performance: the management-level read on where a project's capital is going. */
export const PhaseBudgetChart = ({ report, currency, onSelect, selected, onAddEntry, onConfigureBudget }) => {
  const rows = (report?.by_phase || [])
    .map((phase) => ({
      key: phase.phase,
      label: phase.phase_label || phase.phase,
      budget: Number(phase.costs?.budget || 0),
      actual: Number(phase.costs?.actual || 0),
    }))
    .filter((r) => r.budget || r.actual);

  const totals = rows.reduce(
    (acc, r) => ({ budget: acc.budget + r.budget, actual: acc.actual + r.actual }),
    { budget: 0, actual: 0 }
  );
  const noBudget = totals.budget === 0 && totals.actual > 0;

  return (
    <ChartFrame
      title="Budget vs Actual by Phase"
      subtitle="Compare approved budget with posted project costs."
      context={
        totals.budget
          ? `${compactMoney(totals.actual, currency)} of ${compactMoney(totals.budget, currency)} spent`
          : null
      }
      height={ROW_HEIGHT}
      testId="chart-phase-budget"
      footer={
        totals.actual === 0 && totals.budget > 0
          ? 'No approved actual costs yet - the plan is shown on its own.'
          : onSelect && rows.length > 0
            ? 'Select a phase to filter the financial view below.'
            : null
      }
    >
      {!barsOf(rows) ? (
        <EmptyChart
          headline="No cost entries yet"
          body="Approved project transactions appear here once journal entries are posted."
          action={onAddEntry && <Button size="sm" variant="outline" className="mt-1" onClick={onAddEntry} data-testid="chart-phase-add-entry">Add entry</Button>}
        />
      ) : noBudget ? (
        <EmptyChart
          headline="Budget not configured"
          body="Actual costs are posted but there is no approved plan to compare them with."
          action={onConfigureBudget && <Button size="sm" variant="outline" className="mt-1" onClick={onConfigureBudget} data-testid="chart-phase-configure-budget">Configure budget</Button>}
        />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={6}>
            <CartesianGrid stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="label" tick={AXIS} stroke={COLORS.axis} tickLine={false} axisLine={{ stroke: COLORS.grid }} />
            <YAxis
              tickFormatter={(v) => compactMoney(v, currency)} tick={AXIS} width={64}
              stroke={COLORS.axis} tickLine={false} axisLine={false} domain={[0, 'auto']}
            />
            <Tooltip
              content={<VarianceTooltip currency={currency} />}
              cursor={{ fill: 'rgba(26,26,26,0.03)' }}
            />
            <Bar
              dataKey="budget" name="Budget" fill={COLORS.budget} radius={[3, 3, 0, 0]} maxBarSize={44}
              onClick={(d) => onSelect && onSelect(d.key)} cursor={onSelect ? 'pointer' : undefined}
            />
            <Bar
              dataKey="actual" name="Actual" radius={[3, 3, 0, 0]} maxBarSize={44}
              onClick={(d) => onSelect && onSelect(d.key)} cursor={onSelect ? 'pointer' : undefined}
            >
              {rows.map((r) => (
                <Cell
                  key={r.key}
                  fill={!selected || selected === r.key ? COLORS.actual : COLORS.actualSoft}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
};

const CATEGORY_PREVIEW = 6;

/** Categories read horizontally: the labels are long and precise comparison matters more than polish. */
export const CategoryBudgetChart = ({ report, currency, onSelect, selected }) => {
  const [showAll, setShowAll] = useState(false);

  const all = (report?.by_category || [])
    .map((cat) => ({
      key: cat.category,
      label: cat.category,
      budget: Number(cat.costs?.budget || 0),
      actual: Number(cat.costs?.actual || 0),
    }))
    .filter((r) => r.budget || r.actual)
    .sort((a, b) => Math.max(b.budget, b.actual) - Math.max(a.budget, a.actual));

  const rows = showAll ? all : all.slice(0, CATEGORY_PREVIEW);
  const hidden = all.length - rows.length;
  // Matches the phase chart beside it until the admin expands the full list
  const height = showAll ? Math.max(ROW_HEIGHT, rows.length * 42 + 40) : ROW_HEIGHT;

  return (
    <ChartFrame
      title="Budget vs Actual by Category"
      subtitle="See where project costs are tracking against plan."
      height={height}
      testId="chart-category-budget"
      footer={
        all.length > CATEGORY_PREVIEW ? (
          <button
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => setShowAll((v) => !v)}
            data-testid="chart-category-toggle-all"
          >
            {showAll ? 'Show top categories' : `View all ${all.length} categories`}
            {!showAll && hidden > 0 && <span className="text-foreground-muted font-normal"> · {hidden} more</span>}
          </button>
        ) : null
      }
    >
      {!barsOf(rows) ? (
        <EmptyChart
          headline="No categorised costs yet"
          body="Categories appear once budget lines or journal entries carry one."
        />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }} barGap={3}>
            <CartesianGrid stroke={COLORS.grid} horizontal={false} />
            <XAxis
              type="number" tickFormatter={(v) => compactMoney(v, currency)} tick={AXIS}
              stroke={COLORS.axis} tickLine={false} axisLine={{ stroke: COLORS.grid }} domain={[0, 'auto']}
            />
            <YAxis
              type="category" dataKey="label" tick={AXIS} width={152}
              stroke={COLORS.axis} tickLine={false} axisLine={false}
            />
            <Tooltip content={<VarianceTooltip currency={currency} />} cursor={{ fill: 'rgba(26,26,26,0.03)' }} />
            <Bar
              dataKey="budget" name="Budget" fill={COLORS.budget} radius={[0, 3, 3, 0]} maxBarSize={14}
              onClick={(d) => onSelect && onSelect(d.key)} cursor={onSelect ? 'pointer' : undefined}
            />
            <Bar
              dataKey="actual" name="Actual" radius={[0, 3, 3, 0]} maxBarSize={14}
              onClick={(d) => onSelect && onSelect(d.key)} cursor={onSelect ? 'pointer' : undefined}
            >
              {rows.map((r) => (
                <Cell key={r.key} fill={!selected || selected === r.key ? COLORS.actual : COLORS.actualSoft} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
};

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/** Did the spending happen when it was planned to? */
export const MonthlySpendChart = ({ months, currency }) => {
  const today = currentMonthKey();
  const rows = (months || []).map((m) => {
    const future = m.month > today;
    return {
      key: m.month,
      label: monthLabel(m.month),
      month: m.month,
      budget: Number(m.costs?.budget || 0),
      // A month that has not happened yet has no actual - showing zero would read as bad performance
      actual: future ? null : Number(m.costs?.actual || 0),
      future,
    };
  });
  const hasFuture = rows.some((r) => r.future && r.budget > 0);

  return (
    <ChartFrame
      title="Monthly Spend"
      subtitle="Budgeted and actual project costs by month."
      height={ROW_HEIGHT}
      testId="chart-monthly-spend"
      footer={hasFuture ? 'Months still ahead show budget only, until transactions are posted.' : null}
    >
      {!barsOf(rows.map((r) => ({ budget: r.budget, actual: r.actual || 0 }))) ? (
        <EmptyChart
          headline="Nothing dated yet"
          body="Monthly spend appears once budget lines or journal entries carry a date."
        />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={4}>
            <CartesianGrid stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="label" tick={AXIS} stroke={COLORS.axis} tickLine={false} axisLine={{ stroke: COLORS.grid }} />
            <YAxis
              tickFormatter={(v) => compactMoney(v, currency)} tick={AXIS} width={64}
              stroke={COLORS.axis} tickLine={false} axisLine={false} domain={[0, 'auto']}
            />
            <Tooltip content={<VarianceTooltip currency={currency} />} cursor={{ fill: 'rgba(26,26,26,0.03)' }} />
            <Bar dataKey="budget" name="Budget" fill={COLORS.budget} radius={[3, 3, 0, 0]} maxBarSize={22} />
            <Bar dataKey="actual" name="Actual" fill={COLORS.actual} radius={[3, 3, 0, 0]} maxBarSize={22} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
};

/** Ahead of plan, on plan or behind - read over the whole life of the project. */
export const CumulativeSpendChart = ({ months, currency }) => {
  const today = currentMonthKey();
  let budgetSoFar = 0;
  let actualSoFar = 0;
  const rows = (months || []).map((m) => {
    budgetSoFar += Number(m.costs?.budget || 0);
    const future = m.month > today;
    if (!future) actualSoFar += Number(m.costs?.actual || 0);
    return {
      key: m.month,
      label: monthLabel(m.month),
      budget: budgetSoFar,
      // The actual line stops at today rather than flat-lining into months that have not happened
      actual: future ? null : actualSoFar,
    };
  });

  const last = rows.filter((r) => r.actual !== null).slice(-1)[0];

  return (
    <ChartFrame
      title="Cumulative Project Spend"
      subtitle="Track total project spending against the approved plan over time."
      context={last ? `${compactMoney(last.actual, currency)} spent to date` : null}
      height={ROW_HEIGHT}
      testId="chart-cumulative-spend"
    >
      {rows.length === 0 ? (
        <EmptyChart
          headline="No dated costs yet"
          body="Cumulative spend builds up as budget lines and journal entries are dated."
        />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="label" tick={AXIS} stroke={COLORS.axis} tickLine={false} axisLine={{ stroke: COLORS.grid }} />
            <YAxis
              tickFormatter={(v) => compactMoney(v, currency)} tick={AXIS} width={64}
              stroke={COLORS.axis} tickLine={false} axisLine={false} domain={[0, 'auto']}
            />
            <Tooltip content={<VarianceTooltip currency={currency} />} />
            <Line
              type="monotone" dataKey="budget" name="Budget" stroke={COLORS.budgetHover}
              strokeWidth={2} strokeDasharray="5 4" dot={false}
            />
            <Line
              type="monotone" dataKey="actual" name="Actual" stroke={COLORS.actual}
              strokeWidth={2.5} dot={false} connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
};
